import { z } from 'zod'
import { BUCKET_COLOR_SWATCHES, type BucketSwatchKey } from './resolution-categorization-palette.ts'
import {
  RESOLUTION_META,
  ResolutionStateSchema,
  type ResolutionCounts,
  type ResolutionFilter,
  type ResolutionState,
} from './resolution-taxonomy.ts'

/**
 * A named, coloured group of resolution states — what the collapsed bar draws a segment for and
 * what the legend lists.
 *
 * Buckets group the five resolution states rather than the granular triage and fix outcomes
 * beneath them. Those outcomes span two independent axes: a finding has a triage outcome *and* a
 * fix outcome, so "every outcome belongs to exactly one bucket" cannot hold over their union
 * without a finding landing in two buckets at once. The five states are mutually exclusive by
 * construction, which is what makes the invariant expressible — and they are what the design's own
 * editor lists ("Define how the five resolution outcomes group into colored buckets").
 *
 * Excluding an outcome from the data set is the Data scope control's job instead; the two are
 * independent by design.
 */
export type ResolutionBucket = {
  id: string
  name: string
  /** The swatch's key rather than its fill, so a saved bucket follows later palette changes. */
  swatch: BucketSwatchKey
  states: ResolutionState[]
}

export type Categorization = {
  presetKey: CategorizationPreset
  buckets: ResolutionBucket[]
}

export const CategorizationPresetSchema = z.enum(['pixee-default', 'workflow', 'compliance', 'custom'])
export type CategorizationPreset = z.infer<typeof CategorizationPresetSchema>

export type CategorizationPresetMeta = {
  key: CategorizationPreset
  name: string
  description: string
}

/**
 * The preset bucket configurations. Nobody had settled what these contain when the control was
 * built, so they follow the shapes in the design prototype's `categorization.jsx`, restated over
 * the five resolution states. Names and groupings are the part most likely to change once design
 * reviews them; the model underneath does not depend on any particular grouping.
 */
export const CATEGORIZATION_PRESETS: Record<Exclude<CategorizationPreset, 'custom'>, Categorization> = {
  'pixee-default': {
    presetKey: 'pixee-default',
    buckets: ResolutionStateSchema.options.map(state => ({
      id: state,
      name: RESOLUTION_META[state].label,
      swatch: RESOLUTION_META[state].swatch,
      states: [state],
    })),
  },
  workflow: {
    presetKey: 'workflow',
    buckets: [
      {
        id: 'open',
        name: 'Open',
        swatch: 'haze',
        states: ['acknowledged', 'untriaged'],
      },
      { id: 'pending', name: 'Pending', swatch: 'pulse', states: ['fixReady'] },
      { id: 'closed', name: 'Closed', swatch: 'nebula', states: ['fixed', 'dismissed'] },
    ],
  },
  compliance: {
    presetKey: 'compliance',
    buckets: [
      {
        id: 'outstanding',
        name: 'Outstanding risk',
        swatch: 'ember',
        states: ['acknowledged', 'fixReady'],
      },
      { id: 'backlog', name: 'Triage backlog', swatch: 'haze', states: ['untriaged'] },
      { id: 'remediated', name: 'Remediated', swatch: 'nebula', states: ['fixed'] },
      { id: 'accepted', name: 'Accepted', swatch: 'neutralDark', states: ['dismissed'] },
    ],
  },
}

export const CATEGORIZATION_PRESET_META: CategorizationPresetMeta[] = [
  { key: 'pixee-default', name: 'Pixee default', description: 'One category per resolution state' },
  { key: 'workflow', name: 'Open · Pending · Closed', description: 'Three-stage workflow grouping' },
  { key: 'compliance', name: 'Compliance-first', description: 'Outstanding risk vs accepted vs remediated' },
  { key: 'custom', name: 'Custom', description: 'Your edits, saved on this widget' },
]

export const getDefaultCategorization = (): Categorization =>
  cloneCategorization(CATEGORIZATION_PRESETS['pixee-default'])

export const cloneCategorization = (categorization: Categorization): Categorization => ({
  presetKey: categorization.presetKey,
  buckets: categorization.buckets.map(bucket => ({ ...bucket, states: [...bucket.states] })),
})

export const getPresetCategorization = (key: CategorizationPreset): Categorization =>
  key === 'custom' ? getDefaultCategorization() : cloneCategorization(CATEGORIZATION_PRESETS[key])

/**
 * Which bucket a state belongs to. Every state is in exactly one, so the first match is the answer;
 * `undefined` means the categorization is mid-edit and the state is not placed yet.
 */
export const findBucketForState = (categorization: Categorization, state: ResolutionState) =>
  categorization.buckets.find(bucket => bucket.states.includes(state))

/**
 * The colour to draw a resolution state in. Falls back to the state's canonical colour when no
 * bucket claims it, so a partially edited categorization still renders.
 */
export const getStateColor = ({
  categorization,
  state,
}: {
  categorization?: Categorization
  state: ResolutionState
}): string => {
  if (!categorization) {
    return RESOLUTION_META[state].color
  }

  const bucket = findBucketForState(categorization, state)

  return bucket ? BUCKET_COLOR_SWATCHES[bucket.swatch].fill : RESOLUTION_META[state].color
}

/** The table filter a bucket stands for: the union of the states it groups. */
export const toBucketFilter = (bucket: ResolutionBucket): ResolutionFilter => ({
  label: bucket.name,
  states: bucket.states,
})

export type AggregatedBucket = ResolutionBucket & {
  count: number
  /** The fill of the bucket's swatch, resolved for the bar segment and legend entry. */
  color: string
}

/** Sums each bucket's states into the count its bar segment and legend entry show. */
export const aggregateBuckets = ({
  categorization,
  resolution,
}: {
  categorization: Categorization
  resolution: ResolutionCounts
}): AggregatedBucket[] =>
  categorization.buckets.map(bucket => ({
    ...bucket,
    count: bucket.states.reduce((sum, state) => sum + resolution[state], 0),
    color: BUCKET_COLOR_SWATCHES[bucket.swatch].fill,
  }))

// #region Editing

/** Marks a categorization as edited, so the preset cards stop claiming it matches one of them. */
const asCustom = (buckets: ResolutionBucket[]): Categorization => ({ presetKey: 'custom', buckets })

export const renameBucket = ({
  categorization,
  bucketId,
  name,
}: {
  categorization: Categorization
  bucketId: string
  name: string
}): Categorization =>
  asCustom(categorization.buckets.map(bucket => (bucket.id === bucketId ? { ...bucket, name } : bucket)))

export const recolorBucket = ({
  categorization,
  bucketId,
  swatch,
}: {
  categorization: Categorization
  bucketId: string
  swatch: BucketSwatchKey
}): Categorization =>
  asCustom(categorization.buckets.map(bucket => (bucket.id === bucketId ? { ...bucket, swatch } : bucket)))

/**
 * Moves a state into a bucket, taking it out of whichever bucket held it.
 *
 * This is what keeps every state in exactly one bucket: assignment is a move, never a copy.
 */
export const moveStateToBucket = ({
  categorization,
  state,
  bucketId,
}: {
  categorization: Categorization
  state: ResolutionState
  bucketId: string
}): Categorization =>
  asCustom(
    categorization.buckets.map(bucket => {
      if (bucket.id === bucketId) {
        return bucket.states.includes(state) ? bucket : { ...bucket, states: [...bucket.states, state] }
      }

      return { ...bucket, states: bucket.states.filter(candidate => candidate !== state) }
    })
  )

export const addBucket = (categorization: Categorization): Categorization =>
  asCustom([
    ...categorization.buckets,
    {
      // Unique among the buckets that exist rather than globally: ids only have to tell this
      // categorization's buckets apart, and a counter keeps them readable in tests and the DOM.
      id: `bucket-${categorization.buckets.length + 1}-${Date.now()}`,
      name: 'New category',
      swatch: 'pulse',
      states: [],
    },
  ])

/**
 * Removes a bucket, re-homing its states into the first bucket that remains so nothing falls off
 * the chart. Removing the last bucket is refused: the states it holds would have nowhere to go.
 */
export const removeBucket = ({
  categorization,
  bucketId,
}: {
  categorization: Categorization
  bucketId: string
}): Categorization => {
  const removed = categorization.buckets.find(bucket => bucket.id === bucketId)
  const remaining = categorization.buckets.filter(bucket => bucket.id !== bucketId)

  if (!removed || remaining.length === 0) {
    return categorization
  }

  const [first, ...rest] = remaining

  if (!first) {
    return categorization
  }

  return asCustom([{ ...first, states: [...first.states, ...removed.states] }, ...rest])
}

// #endregion
